import request from '@utils/axios-utils';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { CommonHash, Account, ApiObject, User, ApiList } from '@types';
import Notification from '@components/notification/notification';
import { useAuth } from '@providers/auth';

export function useUploadLogo() {
  const { session, setSession } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/logo`,
        method: 'PATCH',
        data,
      });
    },
    {
      onSuccess: (res: any) => {
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), logo: res.data.data.logo },
        });
        Notification({ type: 'success', message: 'Se ha cargado correctamente el logo' });
      },
    },
  );
}

export function useDeleteLogo() {
  const queryClient = useQueryClient();
  const { session, setSession } = useAuth();
  return useMutation(
    () => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/logo`,
        method: 'DELETE',
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Se ha eliminado el logo' });
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), logo: undefined },
        });
        queryClient.invalidateQueries('account');
      },
    },
  );
}

export function useAccount(uuid: string | undefined) {
  return useQuery<ApiObject<Account>>(['account', uuid], async () => {
    const response = await request({
      url: `/accounts/${uuid}/unauthenticated`,
      method: 'GET',
    });
    return response.data;
  });
}

export function useUpdateBankingDetailsAccount() {
  const queryClient = useQueryClient();
  const { session, setSession } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/banking_details`,
        method: 'PATCH',
        data,
      });
    },
    {
      onSuccess: (res: any) => {
        Notification({ type: 'success', message: 'Se ha actualizado la información bancaria' });
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), ...res.data.data },
        });
        queryClient.invalidateQueries('account');
      },
    },
  );
}

export function useDeleteBankingDetailsAccount() {
  const queryClient = useQueryClient();
  const { session, setSession } = useAuth();
  return useMutation(
    () => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/banking_details`,
        method: 'DELETE',
      });
    },
    {
      onSuccess: (res: any) => {
        Notification({ type: 'success', message: 'Se ha eliminado la información bancaria' });
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), ...res.data.data },
        });
        queryClient.invalidateQueries('account');
      },
    },
  );
}

export function useUpdateAutoStampAccount() {
  const queryClient = useQueryClient();
  const { session, setSession } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/auto_stamp`,
        method: 'PATCH',
        data,
      });
    },
    {
      onSuccess: (res: any) => {
        Notification({ type: 'success', message: 'Se ha actualizado el timbraje automático' });
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), ...res.data.data },
        });
        queryClient.invalidateQueries('account');
      }
    }
  );
}

export function useAddUserToAccount() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/add_user`,
        method: 'POST',
        data,
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Se ha agregado el usuario a la cuenta' });
        queryClient.invalidateQueries("account's users");
      },
    },
  );
}

export function useEditUserFromAccount() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/edit_user`,
        method: 'POST',
        data,
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Se ha actualizado la información del usuario' });
        queryClient.invalidateQueries("account's users");
      },
    },
  );
}

export function useDeleteUserFromAccount() {
  const queryclient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/delete_user_from_account`,
        method: 'DELETE',
        data,
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Se ha eliminado el usuario de la cuenta' });
        queryclient.invalidateQueries("account's users");
      },
    },
  );
}

export function useUpdateSiiPassword({ onSuccess }: { onSuccess?: () => void } = {}) {
  const { session, setSession } = useAuth();
  return useMutation(
    ({ sii_password }: { sii_password: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/accounts/sii_password`,
        method: 'PATCH',
        data: { sii_password },
      });
    },
    {
      onSuccess: () => {
        setSession({
          ...session,
          activeAccount: { ...(session.activeAccount as Account), sii_password_configured: true },
        });
        onSuccess?.();
      },
      onError: () => {
        Notification({ type: 'error', message: 'Error al guardar la clave SII' });
      },
    },
  );
}

export function useAccountUsers() {
  const { session } = useAuth();
  return useQuery<ApiList<User>>(["account's users"], async () => {
    const response = await request({
      authorizationToken: session?.token,
      url: `/accounts/users`,
      method: 'GET',
    });
    return response.data;
  });
}